import {
  formatDuration,
  frameIndexAt,
  SCAN_GAP_SKIP_MS,
  sortFrames,
  VideoTimeline,
} from '@hakimo-ui/hakimo/feature-shared';
import { Frame } from '@hakimo-ui/hakimo/types';
import { FramePlayer } from '@hakimo-ui/hakimo/ui-elements';
import { Button } from '@hakimo-ui/shared/ui-base';
import { PlayIcon } from '@heroicons/react/24/outline';
import {
  PauseIcon,
  PlayIcon as PlaySolidIcon,
} from '@heroicons/react/24/solid';
import { PointerEvent, useMemo, useRef, useState } from 'react';

export function frameHoldMs(frames: Frame[], index: number): number {
  const ts = frames[index]?.timestamp ?? 0;
  const next = frames[index + 1]?.timestamp;
  const gap =
    next === undefined ? ts - (frames[index - 1]?.timestamp ?? ts) : next - ts;
  return Math.min(Math.max(gap, 0), SCAN_GAP_SKIP_MS);
}

interface Props {
  frames: Frame[];
  timeZone: string;
  showMatchedFaces: boolean;
  disableDownload?: boolean;
  showTimeline?: boolean;
}

export function AlarmFrames(props: Props) {
  const {
    frames: rawFrames,
    timeZone,
    showMatchedFaces,
    disableDownload,
    showTimeline = false,
  } = props;
  const frames = useMemo(() => sortFrames(rawFrames), [rawFrames]);
  const [frameIndex, setFrameIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const index = frameIndex < frames.length ? frameIndex : 0;
  const start = frames[0]?.timestamp ?? 0;
  const span = (frames[frames.length - 1]?.timestamp ?? 0) - start;
  const elapsed = (frames[index]?.timestamp ?? start) - start;
  const seekMs = (ms: number) =>
    setFrameIndex(frameIndexAt(frames, start + ms));
  const togglePaused = () => setPaused((p) => !p);

  const seekToX = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect?.width || span <= 0) {
      return;
    }
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    seekMs(ratio * span);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    seekToX(e.clientX);
  };

  return (
    <div
      className="relative aspect-video w-full"
      onClick={togglePaused}
      onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
    >
      <FramePlayer
        key={paused ? 'paused' : 'playing'}
        frames={frames}
        currentFrameIndex={index}
        timeZone={timeZone}
        onFrameChange={setFrameIndex}
        elaborateFrames
        pauseBeforeNextInSec={
          showTimeline ? frameHoldMs(frames, index) / 1000 : undefined
        }
        paused={paused || dragging}
        showMatchedFaces={showMatchedFaces}
      />
      {paused && (
        <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center">
          <PlayIcon className="text-ondark-text-1 h-10 w-10 drop-shadow-lg" />
        </div>
      )}
      {showTimeline && (
        <div
          className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-2 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1 pt-6"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="icon"
            title={paused ? 'Play' : 'Pause'}
            className="text-white drop-shadow focus:ring-offset-0 enabled:hover:bg-white/15"
            onClick={togglePaused}
          >
            {paused ? (
              <PlaySolidIcon className="h-5 w-5 fill-white" />
            ) : (
              <PauseIcon className="h-5 w-5 fill-white" />
            )}
          </Button>
          <div
            ref={trackRef}
            className="flex-1 touch-none [&_.progress-area>div]:transition-none"
            onPointerDown={onPointerDown}
            onPointerMove={(e) => dragging && seekToX(e.clientX)}
            onPointerUp={() => setDragging(false)}
            onPointerCancel={() => setDragging(false)}
          >
            <VideoTimeline
              currentTime={elapsed / 1000}
              duration={span / 1000}
              onSeekVideo={(seconds) => seekMs(seconds * 1000)}
            />
          </div>
          <span className="whitespace-nowrap text-xs tabular-nums text-white drop-shadow">
            {formatDuration(elapsed / 1000)} / {formatDuration(span / 1000)}
          </span>
        </div>
      )}
    </div>
  );
}

export default AlarmFrames;
